<template>
  <div class="flex flex-row h-24 bg-surface-50 dark:bg-surface-800">
    <div class="w-fit ml-2 h-full my-auto">
      <SpeedDial
        :model="items"
        direction="down"
        class="left-2 p-2 mt-3"
        button-class="w-14! h-14! rounded-full!"
        :tooltipOptions="{ event: 'hover', position: 'right' }"
      >
      </SpeedDial>
    </div>

    <Timeline
      :value="timeline"
      layout="horizontal"
      :pt-options="{ mergeProps: false }"
      pt:root:class="flex flex-row overflow-x-auto overflow-y-hidden pl-15"
      pt:event:class="flex-none relative shrink-0 min-h-[50px] flex-col w-24"
      pt:eventOpposite:class="flex-1 text-left py-0"
      pt:eventContent:class="flex-1 text-left py-0"
    >
      <template #marker="slotProps">
        <Button
          :outlined="slotProps.item.drawNumber !== selectedDraw"
          rounded
          class="w-8 h-8 py-4! hover:scale-120 transition duration-200 ease-out font-bold border-2"
          @click="selected(slotProps.item.drawNumber)"
          >{{ slotProps.item.drawNumber }}</Button
        >
      </template>
      <template #content="slotProps">
        <small class="-mx-[1.4rem] w-20 inline-block aboslute text-center! text-surface-600 dark:text-surface-0/70">{{ slotProps.item.name }}</small>
      </template>
      <template #opposite="slotProps">
        <small class="-mx-4 aboslute text-left! w-full text-surface-600 dark:text-surface-0/70">{{
          format(slotProps.item.dateCreated, "MM/dd/yyyy")
        }}</small>
      </template>
    </Timeline>
    <div
      class="w-48 shrink-0 ml-auto border-l-[14px] text-center text-surface-700 dark:text-surface-0/80"
      :class="getDrawStatusDisplayColor(project.projectDrawInfo.drawStatus)[0]"
    >
      <div class="flex flex-col h-full justify-center" :class="getDrawStatusDisplayColor(project.projectDrawInfo.drawStatus)[1]">
        <h1 class="font-bold text-2xl">Draw Status:</h1>
        <p class="text-lg">{{ getDrawStatusDisplayText(project.projectDrawInfo.drawStatus) }}</p>
      </div>
    </div>
  </div>
  <TSDialog label="Email Details" v-model:visible="displayEmailDetails" icon="envelope" size="w-[800px]!">
    <template #content>
      <EmailDetails @cancelled="displayEmailDetails = false" @submitted="sendRequestForApprovalEmail"></EmailDetails>
    </template>
  </TSDialog>
</template>

<script setup lang="ts">
import { DrawStatus, getDrawStatusDisplayColor, getDrawStatusDisplayText } from "@/enums/DrawStatus";
import { ProjectStatusCategory } from "@/enums/ProjectStatusCategory";
import { DrawTimelineItemSchema, DrawTimelineSchema, type DrawTimelineItem } from "@/types/Draw";
import { type DrawItem } from "@/types/DrawItem";
import type { ProjectEntity } from "@/types/ProjectEntity";
import type { Project } from "@/types/Projects";
import type { UserPermissions } from "@/types/User";
import { format } from "date-fns";
import { useConfirm } from "primevue/useconfirm";
import { checkDrawReminders } from "@/services/ReminderService";
import type { ProjectReminder } from "@/types/Reminder";

const timeline = ref<DrawTimelineItem[]>([]);
const selectedDraw = ref<number>(0);

const confirm = useConfirm();

const emit = defineEmits<{
  selected: [drawNumber: number, isCurrent: boolean];
  dataUpdated: [];
}>();

const project = defineModel<Project>("project", { required: true });
const projectReminders = defineModel<ProjectReminder[]>("projectReminders", { required: true });

interface Props {
  drawItems: DrawItem[];
  permissions: UserPermissions;
  sourceOfFunds: ProjectEntity[];
  contacts: ProjectEntity[];
}
const props = defineProps<Props>();

const displayEmailDetails = ref(false);

const showAcknowledgeDecline = computed<boolean>(() => project.value.projectDrawInfo.drawStatus === DrawStatus.DECLINED);
const showAcknowledgeDraw = computed<boolean>(() => project.value.projectSettings.hasImportedDraws);
const showComplete = computed<boolean>(() => project.value.projectDrawInfo.drawStatus !== DrawStatus.COMPLETED);
const showUncomplete = computed<boolean>(() => project.value.projectDrawInfo.drawStatus === DrawStatus.COMPLETED);
const showApprove = computed<boolean>(() => project.value.projectDrawInfo.drawStatus !== DrawStatus.APPROVED);
const showUnapprove = computed<boolean>(() => project.value.projectDrawInfo.drawStatus === DrawStatus.APPROVED);
const showDelete = computed<boolean>(() => selectedDraw.value !== 0);

const items = ref([
  {
    label: "Add New Draw",
    icon: "pi pi-plus-circle",
    command: () => tryAddDraw(),
  },
  {
    label: "Acknowledge Decline",
    icon: "pi pi-ban",
    visible: showAcknowledgeDecline,
    command: () => acknowledgeDeclined(),
  },
  {
    label: "Acknowledge Draw Update",
    icon: "pi pi-bell",
    visible: showAcknowledgeDraw,
    command: () => acknowledgeDraw(),
  },
  {
    label: "Complete Draw",
    icon: "pi pi-check-circle",
    visible: showComplete,
    command: () => tryChangeStatus(DrawStatus.COMPLETED, true, "Complete", "complete"),
  },
  {
    label: "Un-Complete Draw",
    icon: "pi pi-times-circle",
    visible: showUncomplete,
    command: () => tryChangeStatus(DrawStatus.COMPLETED, false, "Un-Complete", "un-complete"),
  },
  {
    label: "Approve Draw",
    icon: "pi pi-thumbs-up",
    visible: showApprove,
    command: () => tryChangeStatus(DrawStatus.APPROVED, true, "Approve", "approve"),
  },
  {
    label: "Un-Approve Draw",
    icon: "pi pi-thumbs-down",
    visible: showUnapprove,
    command: () => tryChangeStatus(DrawStatus.APPROVED, false, "Un-Approve", "un-approve"),
  },
  {
    label: "Delete Draw",
    icon: "pi pi-times-circle",
    visible: showDelete,
    command: () => tryDeleteDraw(),
  },
]);

//TODO: Convert all axios calls to use the handle api response

const tryAddDraw = (): void => {
  confirmPopup(
    confirm,
    "Add New Draw",
    "Are you sure you want to add a new draw?",
    () => {
      if (project.value.projectInfo.projectStatus.category !== ProjectStatusCategory.ACTIVE) {
        return setTimeout(() => updateProjectStatus(), 50);
      } else {
        return addNewDraw();
      }
    },
    () => info("Add Draw Cancelled", "")
  );
};

const updateProjectStatus = (): void => {
  confirmPopup(
    confirm,
    "Update Project Status",
    "A draw can only be added to active projects. \nWould you like to make this an active project?",
    addNewDraw,
    () => info("Add Draw Cancelled", "")
  );
};

const addNewDraw = async (): Promise<void> => {
  axios
    .post("/draw/add/" + project.value?.projectInfo.projectId)
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          const newTimelineItem = DrawTimelineItemSchema.parse(data);
          timeline.value.unshift(newTimelineItem);
          selected(newTimelineItem.drawNumber);
          checkDrawReminders(confirm, projectReminders.value, newTimelineItem.drawNumber, project.value.projectInfo.processor.entityId);
          success("New Draw Added", "");
        },
        (errors: ApiError[]) => {
          error("An error occurred", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const acknowledgeDeclined = (): void => {
  axios.post(`/draw/acknowledge/declined/${project.value.projectDrawInfo.drawId}`).then((response) => {
    handleApiResponse(
      response,
      (data: any) => {
        project.value.projectDrawInfo.drawStatus = DrawStatus.ACTIVE;
      },
      (errors: ApiError[]) => {
        error("An error occurred", errors[0].message);
      }
    );
  });
};

const acknowledgeDraw = (): void => {
  axios.post(`/draw/acknowledge/updated/${project.value.projectDrawInfo.drawId}`).then((response) => {
    handleApiResponse(
      response,
      (data: any) => {
        project.value.projectSettings.hasImportedDraws = false;
        success("Draw Acknowledged", "");
      },
      (errors: ApiError[]) => {
        error("An error occurred", errors[0].message);
      }
    );
  });
};

const tryChangeStatus = (status: DrawStatus, value: boolean, header: string, message: string): void => {
  if (status === DrawStatus.COMPLETED && value && project.value.projectDrawInfo.drawStatus === DrawStatus.APPROVED) {
    error("Invalid Action", "This draw cannot be completed since it is already approved.");
    return;
  }

  confirmPopup(
    confirm,
    `${header} Draw`,
    `Are you sure you want to ${message} this draw?`,
    () => changeStatus(status, header, value),
    () => info(`${header} Draw Cancelled`, "")
  );
};

const changeStatus = (status: DrawStatus, header: string, value: boolean): void => {
  axios
    .post(`/draw/change-status/${project.value.projectDrawInfo.drawId}`, {
      status: status,
      value: value,
    })
    .then((response) => {
      handleApiResponse(
        response,
        (data: any) => {
          emit("dataUpdated");
          success(`Draw ${header}d`, "");

          if (status === DrawStatus.APPROVED) {
            sendDrawApprovedEmail(value);
          } else if (status === DrawStatus.COMPLETED && value) {
            let contactEmailExists = props.contacts.filter((contact) => contact.sendCompletionEmail).length > 0;
            if (
              project.value.projectInfo.sendBorrowerCompletionEmail ||
              project.value.projectInfo.sendBuilderCompletionEmail ||
              project.value.projectInfo.sendInspectorCompletionEmail ||
              project.value.projectInfo.sendLenderCompletionEmail ||
              project.value.projectInfo.sendProcessorCompletionEmail ||
              contactEmailExists
            ) {
              displayEmailDetails.value = true;
            }
          }
        },
        (errors: ApiError[]) => {
          error("An error occurred", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const sendDrawApprovedEmail = (approve: boolean): void => {
  axios
    .post(`/draw/send-approve-email/${project.value.projectDrawInfo.drawId}/${approve}`)
    .then((response) => {
      handleApiResponse(
        response,
        (data: string) => {
          if (data != null && data.length !== 0) success("Email Sent", data);
        },
        (errors: ApiError[]) => {
          error("Email Not Sent", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const sendRequestForApprovalEmail = (message: string): void => {
  axios
    .post(`/draw/send-approval-request/${project.value.projectDrawInfo.drawId}`, { notes: message })
    .then((response) => {
      handleApiResponse(
        response,
        (data: string) => {
          emit("dataUpdated");
          success("Email Sent", data);
        },
        (errors: ApiError[]) => {
          error("Email Not Sent", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
  displayEmailDetails.value = false;
};

const tryDeleteDraw = (): void => {
  if (!props.permissions.administrator || !props.permissions.editProject) error("Access Restricted", "You do not have permission to delete a draw");

  confirmPopup(
    confirm,
    `DELETE DRAW ${selectedDraw.value}?`,
    "WARNING: If you click OK to delete, this draw and all draws that come after this will be deleted",
    deleteDraw,
    () => {}
  );
};

const deleteDraw = (): void => {
  axios
    .post("/draw/delete/" + timeline.value[timeline.value.length - selectedDraw.value - 1].drawId)
    .then((response) => {
      handleApiResponse(
        response,
        async (data: string) => {
          await getTimeline();
          selected(selectedDraw.value - 1);
          success("Draw(s) deleted", data);
        },
        (errors: ApiError[]) => {
          error("Error Occurred", errors[0].message);
        }
      );
    })
    .catch((e) => errorApi(e));
};

const selected = (drawNumber: number): void => {
  if (drawNumber === selectedDraw.value) return;
  selectedDraw.value = drawNumber;
  emit("selected", drawNumber, timeline.value[0].drawNumber === drawNumber);
};

const updateDrawName = (drawNumber: number, name: string) => {
  timeline.value[timeline.value.length - 1 - drawNumber].name = name;
};

defineExpose({ updateDrawName });

const getTimeline = async (): Promise<void> => {
  await axios
    .get("/projects/timeline/" + project.value.projectInfo.projectId)
    .then((response) => (timeline.value = DrawTimelineSchema.parse(response.data.result)))
    .catch((e) => errorApi(e));
};

onBeforeMount(async (): Promise<void> => {
  await getTimeline();
  if (timeline.value.length > 0) selectedDraw.value = timeline.value[0].drawNumber;
});
</script>
